import {PropsWithChildren, ReactNode, useState} from "react";
import {AdminPage} from "../../components/AdminPage";
import {AdminPanel} from "../../components/AdminPanel";
import {AdminTab, AdminTabs} from "../../components/AdminTabs";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../../theme/OtsTheme";

export type EntityProfileDensity = "compact" | "default";

export type EntityProfileTab<T extends string> = AdminTab<T> & {
  content: ReactNode;
};

export type EntityProfileProps<T extends string> = PropsWithChildren<{
  entityTypeLabel: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
  tabs: EntityProfileTab<T>[];
  activeTab?: T;
  initialTab?: T;
  aside?: ReactNode;
  density?: EntityProfileDensity;
  /** Uses the page's full width instead of the standard content cap. */
  fullWidth?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  onTabChange?: (tab: T) => void;
}>;

/** Composable entity profile frame shared by detail, summary, audit, and custom entity views. */
export const EntityProfile = <T extends string>({
  entityTypeLabel,
  title,
  subtitle,
  status,
  actions,
  tabs,
  activeTab: controlledTab,
  initialTab,
  aside,
  density = "default",
  fullWidth = false,
  brand,
  colorScheme,
  children,
  onTabChange,
}: EntityProfileProps<T>) => {
  const firstTab = initialTab ?? tabs[0]?.id;
  const [uncontrolledTab, setUncontrolledTab] = useState<T | undefined>(firstTab);
  const activeTab = controlledTab ?? uncontrolledTab ?? tabs[0]?.id;
  const selected = tabs.find(tab => tab.id === activeTab) ?? tabs[0];

  const selectTab = (tab: T) => {
    if (controlledTab === undefined) setUncontrolledTab(tab);
    onTabChange?.(tab);
  };

  return (
    <OtsThemeRoot brand={brand} colorScheme={colorScheme} data-testid="entity-profile">
      <AdminPage
        eyebrow={entityTypeLabel}
        title={title}
        subtitle={subtitle}
        status={status}
        actions={actions}
        density={density}
        fullWidth={fullWidth}>
        {children}
        <div className={[
          "grid items-start",
          density === "compact" ? "mt-4 gap-4" : "mt-8 gap-7",
          aside ? "xl:grid-cols-[minmax(0,1fr)_320px]" : "grid-cols-1",
        ].join(" ")}>
          <AdminPanel bodyClassName="min-w-0 overflow-hidden">
            <AdminTabs
              tabs={tabs}
              activeTab={selected.id}
              onChange={selectTab}
              ariaLabel={`${String(entityTypeLabel)} sections`}
              idPrefix="entity-profile"
              density={density}/>
            <div
              id={`entity-profile-${selected.id}-panel`}
              role="tabpanel"
              aria-labelledby={`entity-profile-${selected.id}-tab`}
              className={density === "compact" ? "p-4" : "p-5 sm:p-7"}>
              {selected.content}
            </div>
          </AdminPanel>
          {aside && <aside className={density === "compact" ? "space-y-4" : "space-y-7"}>{aside}</aside>}
        </div>
      </AdminPage>
    </OtsThemeRoot>
  );
};
